import {
  collection,
  doc,
  documentId,
  getDoc,
  getDocs,
  query,
  setDoc,
  Timestamp,
  where,
  orderBy,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../firebase";
import { Content, Actor, Chapter, UserProf } from "../utils/types";
import { displayContentCategory } from "../enums/contentCategory";
import { v4 as uuidv4 } from "uuid";

export interface PurchasedContentData {
  id: string;
  chapters: number[];
}

export interface PaymentInfo {
  type: "stripe" | "paypay";
  sessionId?: string;
  paymentId?: string;
  amount?: {
    amount: number;
    currency: string;
  };
}

class ContentService {
  async fetchPurchasedContents(uid: string): Promise<PurchasedContentData[]> {
    const snapShot = await getDocs(collection(db, "users", uid, "contents"));
    return await Promise.all(
      snapShot.docs.map(async (content) => {
        const chapterSnapShot = await getDocs(
          collection(db, "users", uid, "contents", content.id, "chapters"),
        );
        const chapters = chapterSnapShot.docs.map((ch) => Number(ch.id));
        if (!chapters.includes(1)) chapters.unshift(1);
        return {
          id: content.id,
          chapters,
        };
      }),
    );
  }

  async fetchContents(
    purchasedContents: PurchasedContentData[],
  ): Promise<Content[]> {
    if (purchasedContents.length === 0) {
      return [];
    }

    const snapShot = await getDocs(
      query(
        collection(db, "contents"),
        where(
          documentId(),
          "in",
          purchasedContents.map((c) => c.id),
        ),
      ),
    );

    return await Promise.all(
      snapShot.docs.map(async (content) => {
        const snapShot = await getDocs(
          collection(db, "contents", content.id, "chapters"),
        );

        const chapters = snapShot.docs.map((ch) => {
          return {
            index: Number(ch.id),
            actors: ch.data().actors.map((a: Actor) => ({
              name: a.name,
              role: a.role,
              image: `https://firebasestorage.googleapis.com/v0/b/${process.env.REACT_APP_FIREBASE_STORAGE_BUCKET}/o/contents%2Factors%2F${a.image}.png?alt=media`,
            })),
            price: ch.data().price,
            outline: ch.data().outline,
            sampleTotalTime: ch.data().sampleTotalTime,
            totalTime: ch.data().totalTime,
          } as Chapter;
        });

        return {
          id: content.id,
          title: content.data().title,
          category: displayContentCategory(content.data().category),
          actors: content.data().actors.map((a: Actor) => ({
            name: a.name,
            role: a.role,
            image: `https://firebasestorage.googleapis.com/v0/b/${process.env.REACT_APP_FIREBASE_STORAGE_BUCKET}/o/contents%2Factors%2F${a.image}.png?alt=media`,
          })),
          highlight: content.data().highlight,
          chapters: chapters,
          thumbnailUrl: `https://firebasestorage.googleapis.com/v0/b/${process.env.REACT_APP_FIREBASE_STORAGE_BUCKET}/o/contents%2Fthumbnails%2F${content.id}.jpg?alt=media`,
          bgColor: content.data().bgColor,
        } as Content;
      }),
    );
  }

  async isPurchasedContent(
    uid: string,
    contentId: string,
    chapter: string,
  ): Promise<boolean> {
    if (chapter !== "all") {
      const docSnap = await getDoc(
        doc(db, "users", uid, "contents", contentId, "chapters", chapter),
      );
      return docSnap.exists();
    } else {
      const docSnap = await getDocs(
        query(
          collection(db, "users", uid, "contents", contentId, "chapters"),
          where(documentId(), "in", ["2", "3"]),
        ),
      );
      return docSnap.docs.length > 0;
    }
  }

  async getUser(uid: string): Promise<UserProf | null> {
    const ref = doc(db, "users", uid);
    const snap = await getDoc(ref);
    if (snap.exists()) {
      return {
        id: snap.id,
        stripeId: snap.data().stripeId,
      };
    } else {
      return null;
    }
  }

  async setUserContent(
    uid: string,
    contentId: string,
    chapter: string,
    paymentInfo: PaymentInfo,
  ) {
    const docSnap = await getDoc(doc(db, "users", uid, "contents", contentId));
    if (!docSnap.exists()) {
      await setDoc(
        doc(db, "users", uid, "contents", contentId),
        {
          createdAt: Timestamp.now(),
        },
        { merge: true },
      );
    }

    const contentData = {
      type: paymentInfo.type,
      createdAt: Timestamp.now(),
      ...(paymentInfo.type === "stripe"
        ? { sessionId: paymentInfo.sessionId }
        : {
            merchantRefundId: uuidv4(),
            requestedAt: Timestamp.now().seconds,
            paymentId: paymentInfo.paymentId,
            amount: paymentInfo.amount,
          }),
    };

    const chapterData = {
      type: paymentInfo.type,
      createdAt: Timestamp.now(),
      ...(paymentInfo.type === "stripe"
        ? { sessionId: paymentInfo.sessionId }
        : { paymentId: paymentInfo.paymentId }),
    };

    if (paymentInfo.type === "paypay") {
      await setDoc(doc(db, "users", uid, "contents", contentId), contentData);
    }

    if (chapter !== "all") {
      await setDoc(
        doc(db, "users", uid, "contents", contentId, "chapters", chapter),
        chapterData,
      );
    } else {
      const chaptersSnap = await getDocs(
        collection(db, "contents", contentId, "chapters"),
      );
      const chapters = chaptersSnap.docs.map((ch) => ({
        id: Number(ch.id),
        price: ch.data().price,
      }));
      if (chapters.length === 2 && chapters[0].price > 0) {
        await setDoc(
          doc(db, "users", uid, "contents", contentId, "chapters", "1"),
          chapterData,
        );
        await setDoc(
          doc(db, "users", uid, "contents", contentId, "chapters", "2"),
          chapterData,
        );
      } else {
        await setDoc(
          doc(db, "users", uid, "contents", contentId, "chapters", "2"),
          chapterData,
        );
        await setDoc(
          doc(db, "users", uid, "contents", contentId, "chapters", "3"),
          chapterData,
        );
      }
    }
  }

  async getVideoUrl(c: Content, chapter: number): Promise<string> {
    const getContentUrl = httpsCallable(functions, "getContentUrlV2");

    if (c.chapters.filter((ch) => ch.index === chapter)[0].price === 0) {
      const url = getContentUrl({
        contentId: `${c.id}_${c.chapters.find((ch) => ch.index === chapter)
          ?.index}`,
      }).then((result) => (result.data as { url: string }).url);
      return url;
    } else {
      if (c.chapters.length > 1) {
        const url = getContentUrl({
          contentId: `${c.id}_${c?.chapters.filter(
            (ch) => ch.index === chapter,
          )[0].index}`,
        }).then((result) => (result.data as { url: string }).url);
        return url;
      } else {
        const url = getContentUrl({ contentId: c.id }).then(
          (result) => (result.data as { url: string }).url,
        );
        return url;
      }
    }
  }

  async refund(sessionId: string) {
    const refund = httpsCallable(functions, "refund");
    return refund({
      sessionId: sessionId,
    });
  }

  async fetchAllContents(): Promise<Content[]> {
    const snapShot = await getDocs(
      query(collection(db, "contents"), orderBy("createdAt", "desc")),
    );

    return await Promise.all(
      snapShot.docs
        .filter((d) => d.data().isPublished)
        .map(async (content) => {
          const chapterSnapShot = await getDocs(
            collection(db, "contents", content.id, "chapters"),
          );

          const chapters = chapterSnapShot.docs.map((ch) => {
            return {
              index: Number(ch.id),
              actors: ch.data().actors.map((a: Actor) => ({
                name: a.name,
                role: a.role,
                image: `https://firebasestorage.googleapis.com/v0/b/${process.env.REACT_APP_FIREBASE_STORAGE_BUCKET}/o/contents%2Factors%2F${a.image}.png?alt=media`,
              })),
              price: ch.data().price,
              outline: ch.data().outline,
              sampleTotalTime: ch.data().sampleTotalTime,
              totalTime: ch.data().totalTime,
            } as Chapter;
          });

          return {
            id: content.id,
            title: content.data().title,
            category: displayContentCategory(content.data().category),
            actors: content.data().actors.map((a: Actor) => ({
              name: a.name,
              role: a.role,
              image: `https://firebasestorage.googleapis.com/v0/b/${process.env.REACT_APP_FIREBASE_STORAGE_BUCKET}/o/contents%2Factors%2F${a.image}.png?alt=media`,
            })),
            highlight: content.data().highlight,
            chapters: chapters,
            thumbnailUrl: `https://firebasestorage.googleapis.com/v0/b/${process.env.REACT_APP_FIREBASE_STORAGE_BUCKET}/o/contents%2Fthumbnails%2F${content.id}.jpg?alt=media`,
            bgColor: content.data().bgColor,
          } as Content;
        }),
    );
  }
}

export const contentService = new ContentService();
